import axios from 'axios';
import produce, { current } from 'immer';
import {
  filter,
  find,
  forEach,
  isArray,
  isEmpty,
  map,
  partition,
  size,
  some,
  sortBy,
  uniq,
  uniqBy,
} from 'lodash-es';
import { requestGet } from '../utils/httpService';
import getQualifiedImageUrl from '../utils/imageUrlProcessor';
import { NonProfitView } from './nonProfitSlice';
import { authorizedServerFetch } from 'actions/server.actions';
// import { getRequestWithAuth } from 'actions/client-http.actions';
import useStore from './store';

const recommendedRowIdPrefix = 'a000_';

let fetchNonProfitsCancelToken = null;

const isLoggedIn = () => {
  const user = useStore.getState().user.profile;
  return user?.id ? user.id > 0 : false;
};

const fetchRecommended = async (query = {}, cancelToken = null) => {
  let nonProfitData = null;

  if (isLoggedIn()) {
    const { data } = await authorizedServerFetch(
      'users/nonprofits/recommended',
      {},
      query,
    );
    nonProfitData = data?.data;
  } else {
    const queryParamsString = new URLSearchParams(query).toString();
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/users/nonprofits/recommended?${queryParamsString}`,
    );

    if (res.ok) {
      const { data } = await res.json();
      nonProfitData = data;
    }
  }

  if (nonProfitData) {
    const list = uniqBy(
      [...nonProfitData.company, ...nonProfitData.user],
      'id',
    ).map((item) => ({ ...item, isRecommended: true }));
    return list;
  }
  return [];
};

const hasAnyFilterApplied = (filters = useStore.getState().nonProfit.filters) =>
  !isEmpty(filters.causes) ||
  !isEmpty(filters.searchText) ||
  filters.onlyClaimed ||
  filters.onlyFromMyState ||
  filters.includeForeign;

const resetCachedNonProfits = () => {
  useStore.setState(
    produce((draft) => {
      draft.nonProfit.cached = {};
      draft.nonProfit.filteredNonProfitIds = [];
      draft.nonProfit.filteredRowItems = [];
      draft.nonProfit.page = 0;
    }),
  );
};

export const getNonProfitsByIds = (ids) => {
  const { cached } = useStore.getState().nonProfit;
  return map(ids, (id) => cached[id]);
};

const prepareNonProfitRows = () => {
  useStore.setState(
    produce((draft) => {
      const userProfile = draft.user.profile;
      const { cached } = current(draft?.nonProfit);
      const recommendedId = `${recommendedRowIdPrefix}recommended`;
      const noCasusesId = `__otherCauses`;
      const rows = {
        [recommendedId]: {
          header: {
            id: recommendedId,
            title: isLoggedIn() ? 'Nonprofits you follow' : 'Recommended',
            isRecommended: true,
            mixedCauses: true,
          },
          nonProfitIds: [],
        },
        [noCasusesId]: {
          header: {
            id: noCasusesId,
            title: 'Other causes',
            mixedCauses: true,
          },
          nonProfitIds: [],
        },
      };

      forEach(cached, (nonProfit) => {
        if (!isLoggedIn() && nonProfit.isClaimed) {
          rows[recommendedId].nonProfitIds.push(nonProfit.id);
        } else if (nonProfit.isRecommended) {
          rows[recommendedId].nonProfitIds.push(nonProfit.id);
        } else if (isEmpty(nonProfit.causes)) {
          rows[noCasusesId].nonProfitIds.push(nonProfit.id);
        } else {
          const { causes } = nonProfit;
          if (!isEmpty(causes)) {
            causes.forEach((cause) => {
              if (!rows[cause.id]) {
                rows[cause.id] = {
                  header: {
                    id: cause.id,
                    title: cause.cause_title,
                  },
                  nonProfitIds: [],
                };
              }
              rows[cause.id].nonProfitIds.push(nonProfit.id);
            });
          }
        }
      });

      const filteredRows = filter(rows, (row) => size(row.nonProfitIds) > 0);

      // create partition by causes user is following
      const [followingCausesRows, otherCausesRows] = partition(
        filteredRows,
        (row) =>
          row.header.id === recommendedId ||
          some(userProfile.causes, (cause) => cause.id === row.header.id),
      );

      draft.nonProfit.filteredRowItems = [
        ...sortBy(followingCausesRows, [
          (row) => (row.header.id === recommendedId ? 0 : 1),
          (row) => row.header.title,
        ]),
        ...sortBy(otherCausesRows, [
          (row) => (row.header.id === noCasusesId ? 1 : 0),
          (row) => row.header.title,
        ]),
      ];
      draft.nonProfit.filteredNonProfitIds = uniq(
        filteredRows.map((row) => row.nonProfitIds).flat(),
      );
    }),
  );
};

const processNonProfit = (nonProfit) => {
  const np = { ...nonProfit };
  if (np.logo_url) {
    np.logo_url = getQualifiedImageUrl(np.logo_url);
  }
  if (np.projects) {
    np.projects = np.projects.map((project) => {
      const p = { ...project };
      if (project.imageUrl) {
        p.imageUrl = project.imageUrl.map((url) => getQualifiedImageUrl(url));
      }
      return p;
    });
  }
  return np;
};

const getNonProfitSearchQuery = () => {
  const { filters, view } = useStore.getState().nonProfit;
  const query = {
    limit: view === NonProfitView.List ? 100 : 30,
  };
  forEach(filters, (val, key) => {
    query[key] = val;
  });
  return query;
};

export const fetchNonProfits = async (query = {}) => {
  const { view } = useStore.getState().nonProfit;
  const { country } = useStore.getState().user?.profile?.location || {
    country: 'US',
  };
  const params = { ...query, ...getNonProfitSearchQuery() };

  resetCachedNonProfits();

  let nonProfitData = null;
  let recommendedData = null;

  if (isLoggedIn()) {
    const [{ data }, recommended] = await Promise.all([
      authorizedServerFetch('nonprofits', {}, params),
      fetchRecommended(params),
    ]);
    nonProfitData = data?.data;
    recommendedData = recommended;
  } else {
    const queryParamsString = new URLSearchParams(params).toString();

    const [res, recommended] = await Promise.all([
      fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/non-profits/all?${queryParamsString}`,
      ),
      fetchRecommended(params),
    ]);

    if (res.ok) {
      const { data } = await res.json();
      nonProfitData = data;
    }
    recommendedData = recommended;
  }

  if (nonProfitData) {
    useStore.setState(
      produce((draft) => {
        // do not change the order of data and recommended in the following line. If a non profit exists in both lists, we want
        // to keep the one from the recommended list under cached.
        if (!draft.nonProfit.initialized) {
          draft.nonProfit.filters.country = country;
        }
        draft.nonProfit.initialized = true;
        draft.nonProfit.cached = {};
        draft.nonProfit.filteredNonProfitIds = [];
        draft.nonProfit.filteredRowItems = [];
        draft.nonProfit.searchInProgress = false;
        draft.nonProfit.page = 0;
        draft.nonProfit.hasMore = size(nonProfitData) >= params.limit;

        [...recommendedData, ...nonProfitData].forEach((nonProfit) => {
          const np = processNonProfit(nonProfit);
          // we could have duplicates in the recommended list, so we need to check if the non profit is already in the cache
          if (!draft.nonProfit.cached[np.id]) {
            draft.nonProfit.cached[nonProfit.id] = np;
            draft.nonProfit.filteredNonProfitIds.push(np.id);
          }
        });
      }),
    );

    if (view === NonProfitView.List) {
      prepareNonProfitRows();
    }
  }
};

export const fetchNextPage = async () => {
  const query = {
    ...getNonProfitSearchQuery(),
    page: useStore.getState().nonProfit.page + 1,
  };

  useStore.setState(
    produce((draft) => {
      draft.nonProfit.searchInProgress = true;
      draft.nonProfit.page = query.page;
    }),
  );

  let responseData = null;

  if (isLoggedIn()) {
    console.log('fetchNextPage query', query);
    const { data } = await authorizedServerFetch(`nonprofits`, {}, query);
    responseData = data?.data;
  } else {
    const queryParamsString = new URLSearchParams(query).toString();

    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/non-profits/all?${queryParamsString}`,
    );

    if (res.ok) {
      const { data } = await res.json();
      responseData = data;
    }
  }

  useStore.setState(
    produce((draft) => {
      if (responseData) {
        const hasMore = (responseData.length || 0) >= query.limit;
        const newFilteredIds = [];

        responseData.forEach((nonProfit) => {
          const np = processNonProfit(nonProfit);
          draft.nonProfit.cached[nonProfit.id] = np;
          newFilteredIds.push(np.id);
        });

        draft.nonProfit.filteredNonProfitIds.push(...newFilteredIds);
        draft.nonProfit.searchInProgress = false;
        draft.nonProfit.hasMore = hasMore;
      } else {
        draft.nonProfit.searchInProgress = false;
      }
    }),
  );
};

export const useGetFilteredNonProfits = () => {
  const { cached, filteredNonProfitIds } = useStore((state) => state.nonProfit);
  return filteredNonProfitIds.map((id) => cached[id]);
};

export const getFilteredNonProfits = () => {
  const { cached, filteredNonProfitIds } = useStore.getState().nonProfit;
  return filteredNonProfitIds.map((id) => cached[id]);
};

export const applyNonProfitFilter = async (filterDef = {}, query = {}) => {
  useStore.setState(
    produce((draft) => {
      if (isArray(filterDef)) {
        forEach(filterDef, (item) => {
          draft.nonProfit.filters[item.type] = item.value;
        });
      } else {
        draft.nonProfit.filters[filterDef?.type] = filterDef?.value;
      }
      draft.nonProfit.view = hasAnyFilterApplied(draft.nonProfit.filters)
        ? NonProfitView.Grid
        : NonProfitView.List;
      draft.nonProfit.searchInProgress = true;
    }),
  );
  await fetchNonProfits(query);

  const { cached, filteredNonProfitIds } = useStore.getState().nonProfit;
  const data = filteredNonProfitIds.map((id) => cached[id]);
  return data;
};

export const getRecommendedNonProfits = async (query = {}) => {
  const data = await fetchRecommended(query);
  useStore.setState(
    produce((draft) => {
      draft.nonProfit.filteredNonProfitIds = [];
      data.forEach((item) => {
        draft.nonProfit.cached[item.id] = item;
        draft.nonProfit.filteredNonProfitIds.push(item.id);
      });
    }),
  );
  return data;
};

export const fetchProject = async (id) => {
  const { data, ok } = await authorizedServerFetch(`projects/${id}`);
  const responseData = data?.data;
  if (ok) {
    useStore.setState(
      produce((draft) => {
        draft.nonProfit.cached[responseData.nonProfitId].projects =
          draft.nonProfit.cached[responseData.nonProfitId].projects.map((p) => {
            if (p.id === responseData.id) {
              return responseData;
            }
            return p;
          });
      }),
    );
  }
};

const awaitCache = (id) =>
  new Promise((resolve, reject) => {
    let loopCount = 0;
    const scheduled = setInterval(() => {
      const { cached } = useStore.getState().nonProfit;
      const nonProfit = cached[id];
      loopCount += 1;
      if (nonProfit) {
        clearInterval(scheduled);
        resolve(nonProfit);
      }
      if (loopCount > 10) {
        clearInterval(scheduled);
        // reject(new Error(`Timeout while loading non-profit ${id}`));
        resolve(null);
      }
    }, 250);
  });

const awaitCompanyCache = (id) =>
  new Promise((resolve, reject) => {
    let loopCount = 0;
    const scheduled = setInterval(() => {
      const { cached } = useStore.getState().nonProfit;
      const nonProfit = find(cached, (np) => np?.company_id === id);
      loopCount += 1;
      if (nonProfit) {
        clearInterval(scheduled);
        resolve(nonProfit);
      }
      if (loopCount > 10) {
        clearInterval(scheduled);
        // reject(new Error(`Timeout while loading non-profit ${id}`));
        resolve(null);
      }
    }, 250);
  });

export const fetchUserNonProfit = async (includeProjects = true) => {
  const userProfile = useStore.getState().user.profile;
  const npId = userProfile?.activeCommunity?.nonProfit?.id;
  if (npId > 0) {
    const { data, ok } = await authorizedServerFetch(
      `nonprofits/${npId}`,
      {
        method: 'GET',
      },
      {
        includeProjects: includeProjects,
      },
    );
    if (ok) {
      return processNonProfit(data?.data);
    }
  }
  return null;
};

export const fetchNonProfitById = async (id, ignoreCache = false) => {
  const { cached, loading } = useStore.getState().nonProfit;
  const nonProfit = ignoreCache ? null : cached[id];
  if (nonProfit) {
    return nonProfit;
  }

  if (loading[id]) {
    return awaitCache(id);
  }

  useStore.setState(
    produce((draft) => {
      draft.nonProfit.loading[id] = true;
    }),
  );

  const { data, ok } = await authorizedServerFetch(`nonprofits/${id}`);
  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const localCopy = draft.nonProfit.cached[id];
        draft.nonProfit.cached[id] = localCopy
          ? { ...localCopy, ...responseData }
          : responseData;
        delete draft.nonProfit.loading[id];
      }),
    );
  }
  return responseData;
};

export const fetchNonProfitByCompanyId = async (
  companyId,
  ignoreCache = false,
) => {
  const { cached, loading } = useStore.getState().nonProfit;
  const nonProfit = ignoreCache
    ? null
    : find(cached, (np) => np?.company_id === companyId);
  if (nonProfit) {
    return nonProfit;
  }

  if (loading[`company_${companyId}`]) {
    return awaitCompanyCache(companyId);
  }

  useStore.setState(
    produce((draft) => {
      draft.nonProfit.loading[`company_${companyId}`] = true;
    }),
  );

  const { data, ok } = await authorizedServerFetch(
    `nonprofits/company/${companyId}`,
  );
  const responseData = data?.data;
  if (ok) {
    useStore.setState(
      produce((draft) => {
        const npId = responseData.id;
        const localCopy = draft.nonProfit.cached[npId];
        draft.nonProfit.cached[npId] = localCopy
          ? { ...localCopy, ...responseData }
          : responseData;
        delete draft.nonProfit.loading[`company_${companyId}`];
      }),
    );
  }
  return responseData;
};

export const fetchCauseCategories = async () => {
  let responseData = null;
  try {
    if (isLoggedIn()) {
      const { data } = await authorizedServerFetch(`causes/company`);
      responseData = data?.data;
    } else {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/users/causes`,
      );

      if (res.ok) {
        const data = await res.json();
        responseData = {
          all: data,
        };
      } else {
        responseData = {
          all: [],
        };
      }
    }

    const { all = [] } = responseData;

    useStore.setState(
      produce((draft) => {
        draft.nonProfit.causeCategories = all;
        draft.nonProfit.causeCategoryFilterOptions = all
          .map((category) => ({
            label: category.cause_title,
            value: category.id.toString(),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }),
    );
  } catch (error) {
    console.error('Error fetching cause categories:', error);
  }
};

export const resetNonProfitFilters = () => {
  useStore.setState(
    produce((draft) => {
      draft.nonProfit.filters = {
        onlyFromMyState: false,
        includeForeign: false,
        onlyClaimed: false,
        causes: [],
        country: draft.user.profile?.location?.country || 'US',
        searchText: '',
      };
      draft.nonProfit.view = NonProfitView.List;
      draft.nonProfit.searchInProgress = true;
    }),
  );
};

export const fetchLatestNonProfitContributors = async (id) => {
  const { data } = await authorizedServerFetch(`nonprofits/${id}/contributors`);
  return data?.data;
};

export const fetchNonProfitTopDonors = async (id) => {
  const { data } = await authorizedServerFetch(`nonprofits/${id}/top-donors`);
  return data?.data;
};

export const fetchNonProfitTopVolunteers = async (id) => {
  const { data } = await authorizedServerFetch(
    `nonprofits/${id}/top-volunteers`,
  );
  return data?.data;
};
